import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Tooltip from "@mui/material/Tooltip";
import RoomData from "../../../interfaces/robot/rooms/room-data";
import ZoneControllerMouseState, { isAddingState } from "../../../enums/robot/zone-planner/zone-controller-mouse-state";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import {
  selectTeachAndRepeatPathByZoneId,
  selectZoneById,
} from "../../../store/room/zone-planner/zone-planner-selectors";
import { isCleaningZone, isTeachAndRepeatZone } from "../../../interfaces/robot/rooms/cleaning-zone";
import { getSvgCoordinatesFromRosPoint } from "../../../interfaces/robot/rooms/point";
import { getPolygonAreaString } from "../../../utils/map/zone-helper";
import { zonePointsToSvgCoords, svgCoordsToString } from "../../../utils/map/zone-helper";
import ZoneSvgPoint from "./ZoneSvgPoint";
import ZonePointHandles from "./ZonePointHandles";
import TeachAndRepeatZonePathSvg from "./TeachAndRepeatZonePathSvg";
import ZonePointType from "../../../enums/robot/rooms/zone-point-type";
import { MapHistoryAction, MapHistoryType } from "../../../interfaces/robot/zone-planner/map-history";
import useDistanceUnitValue from "../../../hooks/useDistanceUnitValue";
import { DistanceValueUnit } from "../../../enums/configuration/units";
import { isRuleZone } from "../../../interfaces/robot/rooms/rule-zone";
import ZoneSvgPolygon from "./ZoneSvgPolygon";
import { ZoneTypography, ZoneSubTypography } from "./ZoneTypography";

const COPY_OFFSET = 2;

/**
 * Renders a single interactive zone polygon in the ZonePlanner map editor.
 *
 * This is the Redux-connected orchestrator for one zone: it handles select,
 * delete, copy, drag, send-to-back, tooltip display, corner/mid-point handles
 * and the teach-and-repeat path overlay.
 *
 * Colour and hover state are delegated to {@link ZonePolygon}; rule-zone and
 * evacuation-zone fill overrides are resolved here and passed as explicit
 * `fill`/`stroke` props.
 */
const ZonePlannerMapZone: React.FC<{
  zoneId: string;
  roomData: RoomData;
  scale: number;
  isSelected: boolean;
  hoverZoneId: string | null;
  deletable?: boolean;
  mouseState?: ZoneControllerMouseState;
  isReadOnly?: boolean;
  notSelectable?: boolean;
  hidePath?: boolean;
}> = ({
  zoneId,
  roomData,
  scale,
  isSelected,
  hoverZoneId,
  notSelectable = false,
  deletable = true,
  mouseState = ZoneControllerMouseState.Default,
  isReadOnly = false,
  hidePath = false,
}) => {
  const dispatch = useSharedDispatch();

  const {
    setSelectedZone,
    sendZoneToBack,
    setZonePlannerMouseState,
    deleteCleaningZoneById,
    deleteRedZoneById,
    deleteRuleZoneById,
    copyZoneById,
    addUndoableAction,
  } = useSharedActions();

  const zone = useSharedSelector((state) => selectZoneById(state, zoneId));
  const trPath = useSharedSelector((state) => selectTeachAndRepeatPathByZoneId(state, zoneId));
  const { multiplier, label: distanceUnitLabel } = useDistanceUnitValue(0, DistanceValueUnit.METERS_SQUARED);
  const { t } = useTranslation();

  // Tracks internal polygon hover so evacuation-striped red zones lose their stripes on hover.
  const [polygonHovered, setPolygonHovered] = useState(false);

  useEffect(() => {
    const mouseUpHandler = (_event: MouseEvent | TouchEvent) => {
      if (isSelected && mouseState === ZoneControllerMouseState.DragZone) {
        dispatch(setSelectedZone(null));
      }
    };
    window.addEventListener("mouseup", mouseUpHandler);
    window.addEventListener("touchend", mouseUpHandler);
    return () => {
      window.removeEventListener("mouseup", mouseUpHandler);
      window.removeEventListener("touchend", mouseUpHandler);
    };
  }, [isSelected, mouseState, dispatch, setSelectedZone]);

  const onClick = (event: React.TouchEvent | React.MouseEvent) => {
    if (isAddingState(mouseState)) return;
    event.stopPropagation();

    if (mouseState === ZoneControllerMouseState.SendToBack) {
      dispatch(sendZoneToBack(zoneId));
    } else if (!isSelected && mouseState === ZoneControllerMouseState.DeleteZone && deletable && zone != null) {
      let additionalChangedData = undefined;
      if (isCleaningZone(zone)) {
        dispatch(deleteCleaningZoneById(zoneId));
        if (isTeachAndRepeatZone(zone) && trPath != null) {
          additionalChangedData = trPath;
        }
      } else if (isRuleZone(zone)) {
        dispatch(deleteRuleZoneById(zoneId));
      } else {
        dispatch(deleteRedZoneById(zoneId));
      }
      dispatch(
        addUndoableAction({
          action: MapHistoryAction.Deleted,
          type: isCleaningZone(zone)
            ? MapHistoryType.CleaningZone
            : isRuleZone(zone)
              ? MapHistoryType.RuleZone
              : MapHistoryType.RedZone,
          changedData: zone,
          additionalChangedData: additionalChangedData,
        }),
      );
    } else if (
      (!isSelected && mouseState === ZoneControllerMouseState.Default) ||
      mouseState === ZoneControllerMouseState.ShowDetails
    ) {
      dispatch(setSelectedZone({ id: zoneId }));
    }
  };

  const onDoubleClick = () => {
    dispatch(setSelectedZone({ id: zoneId }));
    dispatch(setZonePlannerMouseState(ZoneControllerMouseState.ShowDetails));
  };

  if (zone == null) return null;

  const svgMouseDownTouchStartHandler = (event: React.MouseEvent | React.TouchEvent) => {
    if (!isSelected && mouseState === ZoneControllerMouseState.DragZone) {
      dispatch(setSelectedZone({ id: zoneId, startDraggingHistory: true }));
    } else if (!isSelected && mouseState === ZoneControllerMouseState.CopyZone) {
      dispatch(copyZoneById({ id: zoneId, offset: COPY_OFFSET / scale }));
    }
  };

  const isCleaningZoneInstance = isCleaningZone(zone);
  const isTeachAndRepeatZoneInstance = isTeachAndRepeatZone(zone);
  const isRuleZoneInstance = isRuleZone(zone);
  const isRedZoneInstance = !isCleaningZoneInstance && !isTeachAndRepeatZoneInstance && !isRuleZoneInstance;

  const svgPoints = zonePointsToSvgCoords(zone.points, roomData);
  const validPointCount = svgPoints.filter((p) => p != null).length;
  const pointsStr = svgCoordsToString(svgPoints);

  let cleaningZoneStartPosition = null as { x: number; y: number } | null;
  if (isCleaningZoneInstance && zone.startPosition != null) {
    cleaningZoneStartPosition = getSvgCoordinatesFromRosPoint(zone.startPosition, roomData);
  }

  // ── fill / stroke overrides ──────────────────────────────────────────────
  const isEffectivelyHovered = polygonHovered || hoverZoneId === zoneId;
  // notSelectable red zones always show base colour — suppress their selected/hover tints.
  const suppressHoverForRedZone = notSelectable && isRedZoneInstance;

  // Fill/stroke overrides for zone types not in ZonePolygon's built-in palette.
  let fillOverride: string | undefined;
  let strokeOverride: string | undefined;
  if (isRuleZoneInstance) {
    fillOverride = isSelected || isEffectivelyHovered ? "purple" : "plum";
    strokeOverride = "purple";
  } else if (isRedZoneInstance && zone.evacuation && !isSelected && !isEffectivelyHovered) {
    fillOverride = "url(#stripes)";
  }

  // notSelectable dims and removes pointer events on red/rule zones (mission-view context).
  const notSelectableStyle: React.CSSProperties =
    notSelectable && (isRedZoneInstance || isRuleZoneInstance) ? { opacity: 0.4, pointerEvents: "none" } : {};

  const mainPointerEvents: React.CSSProperties["pointerEvents"] =
    mouseState === ZoneControllerMouseState.AddWayPoint ||
    (mouseState === ZoneControllerMouseState.DeleteZone && !deletable)
      ? "none"
      : undefined;

  const strokeWidth = validPointCount > 2 ? 1 / scale : hoverZoneId === zoneId ? 3 : 10;

  let polygonHelpText: string | null = null;
  if (mouseState === ZoneControllerMouseState.SendToBack) {
    polygonHelpText = t("tooltip-help-send-to-back");
  } else if (notSelectable) {
    polygonHelpText = null;
  } else if (mouseState === ZoneControllerMouseState.Default) {
    polygonHelpText = t("tooltip-help-select-zone");
  } else if (mouseState === ZoneControllerMouseState.DeleteZone) {
    polygonHelpText = t("tooltip-help-delete-zone");
  } else if (mouseState === ZoneControllerMouseState.DragZone) {
    polygonHelpText = t("tooltip-help-drag-zone");
  } else if (mouseState === ZoneControllerMouseState.CopyZone) {
    polygonHelpText = t("tooltip-help-copy-zone");
  }

  // Props shared by every ZonePolygon instance in this render
  const sharedPolygonProps = {
    points: pointsStr,
    isTnR: isTeachAndRepeatZoneInstance,
    isRedZone: isRedZoneInstance,
    isSelected: suppressHoverForRedZone ? false : isSelected,
    externalHovered: suppressHoverForRedZone ? false : hoverZoneId === zoneId,
    onHoverChange: setPolygonHovered,
    fill: fillOverride,
    stroke: strokeOverride,
  };

  const interactionHandlers = notSelectable
    ? {}
    : {
        onClick,
        onDoubleClick,
        onMouseDown: svgMouseDownTouchStartHandler,
        onTouchStart: svgMouseDownTouchStartHandler,
      };

  const testId = isCleaningZoneInstance
    ? "cleaningZoneTestId"
    : isRuleZoneInstance
      ? "ruleZoneTestId"
      : "redZoneTestId";

  const midPointType = isTeachAndRepeatZoneInstance
    ? ZonePointType.TeachAndRepeatZoneMidPoint
    : isCleaningZoneInstance
      ? ZonePointType.CleaningZoneMidPoint
      : isRuleZoneInstance
        ? ZonePointType.RuleZoneMidPoint
        : ZonePointType.RedZoneMidPoint;

  return (
    <React.Fragment>
      {/* Visual polygon for 2-point (line-like) zones — rendered outside the Tooltip */}
      {validPointCount === 2 && (
        <ZoneSvgPolygon
          {...sharedPolygonProps}
          style={{
            ...notSelectableStyle,
            pointerEvents: mainPointerEvents ?? notSelectableStyle.pointerEvents,
          }}
          strokeWidth={hoverZoneId === zoneId ? 3 : 1}
        />
      )}

      {/* Interactive (tooltip-wrapped) polygon */}
      <Tooltip
        followCursor={true}
        title={
          !isSelected && mouseState !== ZoneControllerMouseState.AddWayPoint ? (
            <React.Fragment>
              <ZoneTypography variant="body2" noWrap>
                {zone.name}
              </ZoneTypography>
              {polygonHelpText !== null && <ZoneSubTypography>{polygonHelpText}</ZoneSubTypography>}
              {isCleaningZoneInstance && (
                <ZoneSubTypography>
                  {`${t("zone-area")}: ${getPolygonAreaString(
                    zone.points.map((p) => p.point),
                    multiplier,
                  )}${distanceUnitLabel}`}
                </ZoneSubTypography>
              )}
            </React.Fragment>
          ) : undefined
        }
      >
        {validPointCount === 2 ? (
          // Invisible hit-area overlay for 2-point zones (ZonePolygon above provides the visuals)
          <polygon
            points={pointsStr}
            style={{
              strokeOpacity: 0,
              pointerEvents: mainPointerEvents ?? "visible",
              strokeLinejoin: "round",
              stroke: "red",
            }}
            strokeWidth={strokeWidth}
            {...interactionHandlers}
            data-testid={testId}
          />
        ) : (
          // Coloured, interactive polygon for 3+ point zones
          <ZoneSvgPolygon
            {...sharedPolygonProps}
            style={{
              ...notSelectableStyle,
              pointerEvents: mainPointerEvents ?? notSelectableStyle.pointerEvents,
            }}
            strokeWidth={strokeWidth}
            {...interactionHandlers}
            data-testid={testId}
          />
        )}
      </Tooltip>

      {/* Corner + mid-point handles (only when selected and not in drag/read-only mode) */}
      {isSelected && mouseState !== ZoneControllerMouseState.DragZone && !isReadOnly && (
        <ZonePointHandles
          zonePoints={zone.points}
          roomData={roomData}
          scale={scale}
          midPointType={midPointType}
          enlargeCornerHandlesOnHover={true}
          showMidPoints={mouseState !== ZoneControllerMouseState.DeletePoint}
          midPointsDeletable={deletable || zone.points.length > 3}
        />
      )}

      {/* Cleaning zone start position handle */}
      {cleaningZoneStartPosition != null && isCleaningZoneInstance && zone?.startPosition != null && !isReadOnly && (
        <ZoneSvgPoint
          id={zone.startPosition.id}
          point={cleaningZoneStartPosition}
          scale={scale}
          type={
            isTeachAndRepeatZoneInstance
              ? ZonePointType.TeachAndRepeatZoneStartPosition
              : ZonePointType.CleaningZoneStartPosition
          }
          enlargingOnHoverEnabled={isSelected}
          midPointInsertAtIndex={null}
          preventPointerEvents={!isSelected}
        />
      )}

      {/* Teach-and-repeat zone path */}
      {isSelected && isTeachAndRepeatZoneInstance && !hidePath && (
        <TeachAndRepeatZonePathSvg zoneId={zoneId} roomData={roomData} scale={scale} mouseState={mouseState} />
      )}
    </React.Fragment>
  );
};

export default ZonePlannerMapZone;
